import { Form } from 'antd';
import { useForm } from "antd/es/form/Form";
import { useContext, useEffect, useState } from "react";
import { LoanContext } from "../../LoanDetails";
import { useCreateLoanDoc, useDeleteFileLoanDoc } from "services/loan/mutations";
import { useGetLoanDocByLoan } from "services/loan/querys";
import { useGetTasksByLoan } from "services/task/querys";
import { LoanDocState, ReplicationStrategy } from "gql/graphql";
import CardUploadResponse from "components/BorrowerTask/components/Atoms/Cards/CardUploadResponse/CardUploadResponse";
import { LimitFiles } from "../constants";
import InputBorderNone from "components/BorrowerTask/components/Atoms/Inputs/InputBorderNone/InputBorderNone";

interface IBackgroundCheckFormProps {
  selectLoanDoc: any;
  loanDocs?: any[];
}

const BackgroundCheckForm: React.FC<IBackgroundCheckFormProps> = ({ selectLoanDoc, loanDocs }) => {
  const [form] = useForm();
  const loanSelected: any = useContext(LoanContext);

  const { execute: createLoanDoc, fetching } = useCreateLoanDoc();
  const { execute: deleteFileLoanDoc } = useDeleteFileLoanDoc();
  const { reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({ 
    variables: { loanId: loanSelected?._id }, 
    pause: true 
  });
  const { reexecute: getTasksByLoan } = useGetTasksByLoan({
    variables: { loanId: loanSelected?._id },
    pause: true
  });

  const [fileLoanDoc, setFileLoanDoc] = useState<any[] | undefined>(undefined);

  const isLoanDoc = loanDocs?.find(item => 
    item.key === selectLoanDoc?.key || item._id === selectLoanDoc?._id
  );

  useEffect(() => {
    if (selectLoanDoc) {
      form.setFieldValue("name", selectLoanDoc.label);
    }
  }, [selectLoanDoc, form]);

  useEffect(() => {
    const found = loanDocs?.find(item =>
      item.key === selectLoanDoc?.key || item._id === selectLoanDoc?._id
    );
    let initial: any[] | undefined = found?.file && found?.files?.length === 0 
      ? [found.file] 
      : found?.files ? found.files : undefined;

    setFileLoanDoc(initial);
  }, [loanDocs, selectLoanDoc, loanSelected.onboarding]);

  const onFinish = async (file: any) => {
    const payload = {
      ...form.getFieldsValue(),
      _id: isLoanDoc?._id,
      key: selectLoanDoc?.key,
      loan: loanSelected._id,
      state: LoanDocState.Approved, // Background Check va directo a Approved
      replicationStrategy: ReplicationStrategy.CopyToAll
    };

    const isFilesNews = file && file?.filter((item: any) => !!item.size && !item.url && !item.key)?.length > 0;

    if (isFilesNews) {
      if (file?.length > 1) {
        payload.files = isLoanDoc?.files?.length > 0 ? null : file;
      } else {
        payload.file = isLoanDoc?.file ? null : file[0];
      }
    }

    await createLoanDoc(payload);
    
    setFileLoanDoc(undefined);
    
    await Promise.all([
      getLoanDocByLoan({ requestPolicy: 'network-only' }),
      getTasksByLoan({ requestPolicy: 'network-only' })
    ]);
  };

  const onDeleteFile = async (fileToDelete: any) => {
    if (isLoanDoc && isLoanDoc.file) {
      await deleteFileLoanDoc({ fileKey: isLoanDoc.file.key, loanDocId: isLoanDoc._id });
      setFileLoanDoc([]);
    }
    if (isLoanDoc && isLoanDoc.files && isLoanDoc.files.length > 0 && fileToDelete?.key) {
      await deleteFileLoanDoc({ fileKey: fileToDelete.key, loanDocId: isLoanDoc._id, multipleFiles: true });
      setFileLoanDoc((prev) => prev?.filter((file) => file.key !== fileToDelete.key));
    }
    
    await Promise.all([
      getLoanDocByLoan({ requestPolicy: 'network-only' }),
      getTasksByLoan({ requestPolicy: 'network-only' })
    ]);
  };

  const disabledMultipleDocs = fileLoanDoc && fileLoanDoc?.length > 0;

  return (
    <Form
      form={form}
      name="background-check-form"
      className='form'
      autoComplete="off"
      onFinish={onFinish}
    >
      <CardUploadResponse
        title=""
        titleLocation='outside'
        limitFiles={LimitFiles.DEFAULT}
        actionDeleteFile={onDeleteFile}
        files={fileLoanDoc}
        submit={{
          title: 'Publish to borrower',
          disabled: disabledMultipleDocs,
          loading: fetching,
          modalConfirm: true,
          confirmDescription: "After submitting, you'll have to contact us to make any changes.",
          onClick: (files) => onFinish(files),
          location: 'outside'
        }}
        isCreditReportDocument={false}
        isLoanDoc={isLoanDoc}
        hasDropdown={false}
        insertHtml={
          <div className='doc-info'>
            <Form.Item
              name="name"
              className='form-item-doc-name'
              style={{ marginBottom: 0 }}
              rules={[
                {
                  required: true,
                  message: 'Please input your doc name!'
                }
              ]}
            >
              <InputBorderNone
                placeholder="Enter doc name"
                className="form-task-name"
                color="bold"
                disabled={true}
              />
            </Form.Item>
          </div>
        }
        messageErrorDisabled='Please upload a file'
        isSelectChanged={true}
      />
    </Form>
  );
};

export default BackgroundCheckForm;